import { useState } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import SearchableDropdown from '../../../../components/SearchableDropdown';
import usePermissions from '../../../../hook/usePermissions';
import { PERMISSIONS } from '../../../../consts/permissions';
import {
    getDeliveriesList,
    createDelivery,
    updateDelivery,
    deleteDelivery,
    completeDelivery,
} from '../../../../redux/actions/SocietyPortal/VmsAction/VmsAction';
import useVmsList from '../useVmsList';
import VmsListShell from '../components/vms/VmsListShell';
import VmsTable from '../components/vms/VmsTable';
import VmsBadge from '../components/vms/VmsBadge';
import WingFlatPicker from '../components/vms/WingFlatPicker';
import ConfirmDialog from '../components/ConfirmDialog';
import DeliveryFormModal from './DeliveryFormModal';
import { labelCls } from '../components/form/formStyles';
import {
    DELIVERY_STATUSES,
    DELIVERY_TYPES,
    DELIVERY_STATUS_STYLE,
    DELIVERY_TYPE_ICON,
    isDeliveryTerminal,
    fmtDateTime,
    flatLabel,
    humanize,
    toOptions,
} from '../vmsConstants';
import { fmtPhone } from '../visitorConstants';

const STATUS_OPTIONS = toOptions(DELIVERY_STATUSES);
const TYPE_OPTIONS = toOptions(DELIVERY_TYPES);

// The complete endpoint accepts only these two.
const HANDOVER_OPTIONS = [
    { value: 'DELIVERED_TO_RESIDENT', label: 'Handed to the resident' },
    { value: 'DELIVERED_TO_GUARD', label: 'Left with the guard' },
];

const Deliveries = () => {
    const dispatch = useDispatch();
    const { can } = usePermissions();

    const canCreate = can(PERMISSIONS.VMS_DELIVERY_CREATE);
    const canUpdate = can(PERMISSIONS.VMS_DELIVERY_UPDATE);
    const canComplete = can(PERMISSIONS.VMS_DELIVERY_COMPLETE);

    const { saving, acting } = useSelector(state => state.VmsReducer?.deliveries) || {};

    const [formTarget, setFormTarget] = useState(null); // { mode, delivery }
    const [completeTarget, setCompleteTarget] = useState(null);
    const [handover, setHandover] = useState('DELIVERED_TO_RESIDENT');
    const [deleteTarget, setDeleteTarget] = useState(null);

    const list = useVmsList({
        slice: 'deliveries',
        action: getDeliveriesList,
        filters: { status: '', deliveryType: '', wingId: '', flatId: '' },
    });

    const closeForm = () => setFormTarget(null);

    const handleSave = (payload) => {
        const done = () => { closeForm(); list.refresh(); };
        if (formTarget.mode === 'create') {
            dispatch(createDelivery({ ...payload, onSuccess: done }));
        } else {
            dispatch(updateDelivery({ id: formTarget.delivery.delivery_id, ...payload, onSuccess: done }));
        }
    };

    const handleComplete = () => {
        dispatch(completeDelivery({
            id: completeTarget.delivery_id,
            status: handover,
            onSuccess: () => { setCompleteTarget(null); list.refresh(); },
        }));
    };

    const handleDelete = () => {
        dispatch(deleteDelivery({
            id: deleteTarget.delivery_id,
            onSuccess: () => { setDeleteTarget(null); list.refresh(); },
        }));
    };

    const openComplete = (d) => {
        setHandover('DELIVERED_TO_RESIDENT');
        setCompleteTarget(d);
    };

    const columns = [
        {
            key: 'delivery_code',
            header: 'Delivery',
            nowrap: true,
            render: (d) => (
                <div>
                    <p className="tw-mb-0 tw-font-mono tw-font-semibold tw-text-gray-800">{d.delivery_code || '—'}</p>
                    <p className="tw-mb-0 tw-mt-0.5 tw-text-[10px] tw-text-gray-500">{fmtDateTime(d.created_at)}</p>
                </div>
            ),
        },
        {
            key: 'delivery_type',
            header: 'Type',
            render: (d) => (
                <span className="tw-inline-flex tw-items-center tw-gap-1">
                    <Icon icon={DELIVERY_TYPE_ICON[d.delivery_type] || DELIVERY_TYPE_ICON.OTHER} className="tw-text-gray-400" />
                    {humanize(d.delivery_type)}
                </span>
            ),
        },
        { key: 'flat', header: 'Flat', nowrap: true, render: (d) => flatLabel(d) },
        {
            key: 'provider',
            header: 'From',
            render: (d) => (
                <div className="tw-min-w-0">
                    <p className="tw-mb-0 tw-font-semibold tw-text-gray-800 tw-truncate">{d.provider_name || '—'}</p>
                    <p className="tw-mb-0 tw-mt-0.5 tw-text-[10px] tw-text-gray-500 tw-truncate">
                        {d.delivery_person_name || ''}
                        {d.delivery_person_phone ? ` · ${fmtPhone(d.delivery_person_phone)}` : ''}
                    </p>
                </div>
            ),
        },
        {
            key: 'tracking_number',
            header: 'Tracking',
            render: (d) => d.tracking_number
                ? <span className="tw-font-mono tw-text-[10px]">{d.tracking_number}</span>
                : <span className="tw-text-gray-300">—</span>,
        },
        {
            key: 'status',
            header: 'Status',
            render: (d) => <VmsBadge value={d.status} styles={DELIVERY_STATUS_STYLE} />,
        },
        {
            key: 'received_at',
            header: 'Received',
            nowrap: true,
            render: (d) => d.received_at
                ? <span className="tw-text-[10px]">{fmtDateTime(d.received_at)}</span>
                : <span className="tw-text-gray-300">—</span>,
        },
        {
            key: 'actions',
            header: 'Action',
            align: 'right',
            stopPropagation: true,
            render: (d) => {
                const terminal = isDeliveryTerminal(d);
                return (
                    <div className="tw-inline-flex tw-items-center tw-gap-1">
                        {/* The API puts no guard on complete: calling it on an
                            already-delivered parcel succeeds and overwrites
                            received_at. Hiding the button is the only guard. */}
                        {canComplete && !terminal && (
                            <button
                                title="Mark delivered"
                                disabled={acting}
                                onClick={() => openComplete(d)}
                                className="tw-p-1.5 tw-rounded hover:tw-bg-emerald-50 tw-text-emerald-600 disabled:tw-opacity-50"
                            >
                                <Icon icon="mdi:check-circle-outline" className="tw-text-base" />
                            </button>
                        )}
                        {canUpdate && (
                            <button
                                title="Edit"
                                onClick={() => setFormTarget({ mode: 'edit', delivery: d })}
                                className="tw-p-1.5 tw-rounded hover:tw-bg-amber-50 tw-text-amber-600"
                            >
                                <Icon icon="mdi:pencil-outline" className="tw-text-base" />
                            </button>
                        )}
                        {canUpdate && (
                            <button
                                title="Remove"
                                disabled={acting}
                                onClick={() => setDeleteTarget(d)}
                                className="tw-p-1.5 tw-rounded hover:tw-bg-rose-50 tw-text-rose-600 disabled:tw-opacity-50"
                            >
                                <Icon icon="mdi:trash-can-outline" className="tw-text-base" />
                            </button>
                        )}
                    </div>
                );
            },
        },
    ];

    const filters = (
        <>
            <div>
                <label className={labelCls}>Status</label>
                <SearchableDropdown
                    small
                    value={list.filters.status}
                    onChange={(v) => list.setFilter('status', v === 'all' ? '' : v)}
                    options={STATUS_OPTIONS}
                    placeholder="All statuses"
                />
            </div>
            <div>
                <label className={labelCls}>Type</label>
                <SearchableDropdown
                    small
                    value={list.filters.deliveryType}
                    onChange={(v) => list.setFilter('deliveryType', v === 'all' ? '' : v)}
                    options={TYPE_OPTIONS}
                    placeholder="All types"
                />
            </div>
            <WingFlatPicker
                clearable
                required={false}
                societyId={list.societyId}
                wingId={list.filters.wingId}
                flatId={list.filters.flatId}
                onWingChange={(v) => list.setFilter('wingId', v)}
                onFlatChange={(v) => list.setFilter('flatId', v)}
                wingPlaceholder="Any wing"
                flatPlaceholder="Any flat"
            />
        </>
    );

    const editInitial = formTarget?.delivery ? {
        delivery_type: formTarget.delivery.delivery_type || 'PARCEL',
        provider_name: formTarget.delivery.provider_name || '',
        delivery_person_name: formTarget.delivery.delivery_person_name || '',
        delivery_person_phone: formTarget.delivery.delivery_person_phone || '',
        tracking_number: formTarget.delivery.tracking_number || '',
        status: formTarget.delivery.status || 'AT_GATE',
        entry_gate: formTarget.delivery.entry_gate || '',
        photo_url: formTarget.delivery.photo_url || '',
        notes: formTarget.delivery.notes || '',
        delivery_code: formTarget.delivery.delivery_code,
        flatLabel: flatLabel(formTarget.delivery),
    } : null;

    return (
        <div className="tw-p-5 tw-bg-gradient-to-br tw-from-gray-50 tw-to-slate-50 tw-min-h-screen tw-space-y-4">
            <VmsListShell
                title="Deliveries"
                subtitle="Parcels, food and couriers logged at the gate"
                itemLabel="deliveries"
                list={list}
                filters={filters}
                headerActions={canCreate && (
                    <button
                        onClick={() => setFormTarget({ mode: 'create' })}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-2 tw-rounded-md tw-text-xs tw-font-medium tw-text-white tw-bg-sky-600 hover:tw-bg-sky-700"
                    >
                        <Icon icon="mdi:package-variant-closed-plus" className="tw-text-base" />
                        Log Delivery
                    </button>
                )}
            >
                <VmsTable
                    columns={columns}
                    rows={list.rows}
                    loading={list.loading}
                    loaderLabel="Loading deliveries..."
                    rowKey={(d) => d.delivery_id}
                    empty={{
                        icon: 'mdi:package-variant-closed',
                        filtered: list.hasFilters,
                        filteredTitle: 'No deliveries match these filters',
                        emptyTitle: 'No deliveries logged yet',
                        emptyHint: 'Log a parcel at the gate and the flat’s residents are notified straight away.',
                        onClear: list.resetFilters,
                    }}
                />
            </VmsListShell>

            <DeliveryFormModal
                open={!!formTarget}
                mode={formTarget?.mode || 'create'}
                societyId={list.societyId}
                initial={editInitial}
                submitting={saving}
                onSubmit={handleSave}
                onCancel={closeForm}
            />

            <ConfirmDialog
                open={!!completeTarget}
                title="Mark this delivery as handed over?"
                subtitle={completeTarget?.delivery_code}
                confirmLabel="Confirm handover"
                busyLabel="Saving..."
                tone="success"
                icon="mdi:check-circle-outline"
                busy={acting}
                onCancel={() => setCompleteTarget(null)}
                onConfirm={handleComplete}
            >
                <p className="tw-mb-3">Who took it?</p>
                <SearchableDropdown
                    small
                    clearable={false}
                    value={handover}
                    onChange={setHandover}
                    options={HANDOVER_OPTIONS}
                    placeholder="Select"
                />
                <p className="tw-mb-0 tw-mt-3 tw-text-[10px] tw-text-gray-500">
                    The residents of {flatLabel(completeTarget || {})} are notified once this is saved.
                </p>
            </ConfirmDialog>

            <ConfirmDialog
                open={!!deleteTarget}
                title="Remove this delivery?"
                subtitle="The record is hidden from the list."
                confirmLabel="Remove"
                busyLabel="Removing..."
                tone="danger"
                icon="mdi:trash-can-outline"
                busy={acting}
                onCancel={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
            >
                <p className="tw-mb-0">
                    <span className="tw-font-mono tw-font-semibold">{deleteTarget?.delivery_code}</span> for{' '}
                    {flatLabel(deleteTarget || {})} will be removed.
                </p>
            </ConfirmDialog>
        </div>
    );
};

export default Deliveries;
